import { useState, useEffect } from 'react';
import { useTenant } from '../../contexts/TenantContext';
import { useKidsStore } from '../../store/kidsStore';
import { toast } from 'sonner';

type DifficultadKey = 'neurona' | 'sinapsis' | 'cerebro' | 'genio' | 'einstein';
type DifferenceType = 'cambio_emoji' | 'cambio_color' | 'cambio_tamaño' | 'rotacion';

const LEVELS = {
  neurona: {
    nombre: 'Neurona',
    icono: '🧠',
    objetos_escena: 6,
    num_diferencias: 3,
    tiempo_limite: 120,
    tamaño_min: 50,
    tamaño_max: 70,
    tipos_diferencias: ['cambio_emoji', 'cambio_color'] as DifferenceType[],
    tolerancia_click: 10
  },
  sinapsis: {
    nombre: 'Sinapsis',
    icono: '⚡',
    objetos_escena: 10,
    num_diferencias: 4,
    tiempo_limite: 140,
    tamaño_min: 45,
    tamaño_max: 70,
    tipos_diferencias: ['cambio_emoji', 'cambio_color', 'cambio_tamaño'] as DifferenceType[],
    tolerancia_click: 9
  },
  cerebro: {
    nombre: 'Cerebro',
    icono: '🧩',
    objetos_escena: 14,
    num_diferencias: 5,
    tiempo_limite: 150,
    tamaño_min: 40,
    tamaño_max: 70,
    tipos_diferencias: ['cambio_emoji', 'cambio_color', 'cambio_tamaño', 'rotacion'] as DifferenceType[],
    tolerancia_click: 8
  },
  genio: {
    nombre: 'Genio',
    icono: '💡',
    objetos_escena: 18,
    num_diferencias: 6,
    tiempo_limite: 180,
    tamaño_min: 35,
    tamaño_max: 75,
    tipos_diferencias: ['cambio_emoji', 'cambio_color', 'cambio_tamaño', 'rotacion'] as DifferenceType[],
    tolerancia_click: 7
  },
  einstein: {
    nombre: 'Einstein',
    icono: '🎓',
    objetos_escena: 22,
    num_diferencias: 7,
    tiempo_limite: 210,
    tamaño_min: 30,
    tamaño_max: 80,
    tipos_diferencias: ['cambio_emoji', 'cambio_color', 'cambio_tamaño', 'rotacion'] as DifferenceType[],
    tolerancia_click: 6
  }
};

const THEMES = [
  { code: 'granja', nombre: 'Granja', emojis: ['🐮', '🐷', '🐔', '🐴', '🌻', '🌾', '🏡', '🚜', '🌳', '🦆', '🐑', '🥕'] },
  { code: 'oceano', nombre: 'Océano', emojis: ['🐠', '🐟', '🦈', '🐙', '🦀', '🐚', '🌊', '⛵', '🏖️', '🦑', '🐬', '🪸'] },
  { code: 'bosque', nombre: 'Bosque', emojis: ['🦊', '🦌', '🐿️', '🦉', '🌲', '🌳', '🍄', '🦋', '🐻', '🌸', '🦎', '🐞'] },
  { code: 'espacio', nombre: 'Espacio', emojis: ['🚀', '🛸', '🌙', '⭐', '🪐', '☄️', '👽', '🌟', '🌍', '🛰️', '🌌', '✨'] },
  { code: 'ciudad', nombre: 'Ciudad', emojis: ['🏢', '🚗', '🚕', '🚌', '🏬', '🏪', '🚦', '🌳', '⛲', '🚲', '🏘️', '🎡'] },
  { code: 'selva', nombre: 'Selva', emojis: ['🦁', '🐯', '🦒', '🐘', '🦜', '🐍', '🦓', '🌴', '🦍', '🐊', '🦩', '🌺'] }
];

const BG_COLORS = ['#FFE5E5', '#E5F3FF', '#FFF9E5', '#E5FFE5', '#F5E5FF', '#FFE5F5'];

interface SceneObject {
  id: number;
  emoji: string;
  x: number;
  y: number;
  size: number;
  rotation: number;
  bgcolor: string;
}

interface Difference {
  id: number;
  x: number;
  y: number;
  tipo: DifferenceType;
  encontrada: boolean;
}

interface MarkerPosition {
  x: number;
  y: number;
  id: number;
}

export default function DiferenciasGame() {
  const tenant = useTenant();
  const { selectedKid, registerProgress, registerPlayTime } = useKidsStore();

  const [nivel, setNivel] = useState<DifficultadKey>('neurona');
  const config = LEVELS[nivel];

  const [escena1, setEscena1] = useState<SceneObject[]>([]);
  const [escena2, setEscena2] = useState<SceneObject[]>([]);
  const [diferencias, setDiferencias] = useState<Difference[]>([]);
  const [themeName, setThemeName] = useState('');
  const [diferenciasEncontradas, setDiferenciasEncontradas] = useState(0);
  const [errores, setErrores] = useState(0);
  const [tiempoRestante, setTiempoRestante] = useState(config.tiempo_limite);
  const [gameWon, setGameWon] = useState(false);
  const [gameLost, setGameLost] = useState(false);
  const [markers, setMarkers] = useState<MarkerPosition[]>([]);
  const [showRules, setShowRules] = useState(false);
  const [startTime] = useState(Date.now());

  useEffect(() => {
    generateNewGame();
  }, [nivel]);

  useEffect(() => {
    if (gameWon || gameLost) return;

    const interval = setInterval(() => {
      setTiempoRestante(prev => {
        if (prev <= 1) {
          setGameLost(true);
          toast.error('⏰ ¡Se acabó el tiempo!');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [gameWon, gameLost]);

  const generateNewGame = () => {
    const theme = THEMES[Math.floor(Math.random() * THEMES.length)];
    setThemeName(theme.nombre);

    const scene1 = generateScene(theme.emojis);
    const { scene2, diffs } = generateDifferences(scene1, theme.emojis);

    setEscena1(scene1);
    setEscena2(scene2);
    setDiferencias(diffs);
    setDiferenciasEncontradas(0);
    setErrores(0);
    setTiempoRestante(config.tiempo_limite);
    setGameWon(false);
    setGameLost(false);
    setMarkers([]);
  };

  const generateScene = (emojis: string[]): SceneObject[] => {
    const objects: SceneObject[] = [];

    for (let i = 0; i < config.objetos_escena; i++) {
      const emoji = emojis[Math.floor(Math.random() * emojis.length)];
      const x = Math.floor(Math.random() * 80) + 10;
      const y = Math.floor(Math.random() * 80) + 10;
      const size = Math.floor(Math.random() * (config.tamaño_max - config.tamaño_min + 1)) + config.tamaño_min;
      const rotation = Math.floor(Math.random() * 31) - 15;
      const bgcolor = BG_COLORS[Math.floor(Math.random() * BG_COLORS.length)];

      objects.push({ id: i, emoji, x, y, size, rotation, bgcolor });
    }

    return objects;
  };

  const generateDifferences = (
    scene1: SceneObject[],
    emojis: string[]
  ): { scene2: SceneObject[]; diffs: Difference[] } => {
    const scene2 = scene1.map(obj => ({ ...obj }));
    const diffs: Difference[] = [];

    const indices = [...Array(scene1.length).keys()];
    for (let i = indices.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [indices[i], indices[j]] = [indices[j], indices[i]];
    }

    const indicesToModify = indices.slice(0, config.num_diferencias);

    for (const idx of indicesToModify) {
      const tipo = config.tipos_diferencias[Math.floor(Math.random() * config.tipos_diferencias.length)];
      scene2[idx] = applyDifference(scene2[idx], tipo, emojis);

      diffs.push({
        id: scene2[idx].id,
        x: scene2[idx].x,
        y: scene2[idx].y,
        tipo,
        encontrada: false
      });
    }

    return { scene2, diffs };
  };

  const applyDifference = (obj: SceneObject, tipo: DifferenceType, emojis: string[]): SceneObject => {
    const newObj = { ...obj };

    switch (tipo) {
      case 'cambio_emoji':
        let newEmoji = emojis[Math.floor(Math.random() * emojis.length)];
        while (newEmoji === obj.emoji) {
          newEmoji = emojis[Math.floor(Math.random() * emojis.length)];
        }
        newObj.emoji = newEmoji;
        break;

      case 'cambio_color':
        let newColor = BG_COLORS[Math.floor(Math.random() * BG_COLORS.length)];
        while (newColor === obj.bgcolor) {
          newColor = BG_COLORS[Math.floor(Math.random() * BG_COLORS.length)];
        }
        newObj.bgcolor = newColor;
        break;

      case 'cambio_tamaño':
        const delta = Math.floor(Math.random() * 41) - 20;
        newObj.size = Math.max(config.tamaño_min - 10, Math.min(config.tamaño_max + 10, obj.size + delta));
        break;

      case 'rotacion':
        newObj.rotation = obj.rotation + Math.floor(Math.random() * 151) + 30;
        break;
    }

    return newObj;
  };

  const handleSceneClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (gameWon || gameLost) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = ((e.clientX - rect.left) / rect.width) * 100;
    const clickY = ((e.clientY - rect.top) / rect.height) * 100;

    let found = false;

    diferencias.forEach((dif, index) => {
      if (dif.encontrada) return;

      const distance = Math.sqrt(Math.pow(clickX - dif.x, 2) + Math.pow(clickY - dif.y, 2));

      if (distance <= config.tolerancia_click) {
        found = true;
        markDifferenceFound(dif, index);
      }
    });

    if (!found) {
      registerError();
    }
  };

  const markDifferenceFound = (dif: Difference, index: number) => {
    const newDifs = [...diferencias];
    newDifs[index].encontrada = true;
    setDiferencias(newDifs);

    setMarkers(prev => [...prev, { x: dif.x, y: dif.y, id: dif.id }]);
    setDiferenciasEncontradas(prev => prev + 1);

    toast.success('¡Diferencia encontrada! ✓');

    if (diferenciasEncontradas + 1 === config.num_diferencias) {
      setGameWon(true);
      saveProgress();
    }
  };

  const registerError = () => {
    setErrores(prev => prev + 1);
    toast.error('❌ Esa no es una diferencia');
  };

  const saveProgress = async () => {
    if (!selectedKid) return;

    try {
      const segundos = Math.floor((Date.now() - startTime) / 1000);

      await registerProgress(selectedKid.id, {
        juego_id: null,
        tipo_nivel: 'diferencias',
        nivel_id: Object.keys(LEVELS).indexOf(nivel) + 1,
        tablero_id: null,
        tiempo: segundos,
        movimientos: errores
      });

      const minutosJugados = Math.ceil(segundos / 60);
      await registerPlayTime(selectedKid.id, minutosJugados);

      toast.success('¡Progreso guardado! 🎉');
    } catch (error) {
      console.error('Error al guardar:', error);
    }
  };

  const renderScene = (objects: SceneObject[], clickable: boolean) => (
    <div
      className="relative w-full aspect-square border-4 rounded-2xl bg-white overflow-hidden shadow-lg"
      onClick={clickable ? handleSceneClick : undefined}
      style={{
        borderColor: tenant.theme.colors.primary,
        cursor: clickable && !gameWon && !gameLost ? 'crosshair' : 'default'
      }}
    >
      {objects.map(obj => (
        <div
          key={obj.id}
          className="absolute flex items-center justify-center transition-transform hover:scale-110"
          style={{
            left: `${obj.x}%`,
            top: `${obj.y}%`,
            width: `${obj.size}px`,
            height: `${obj.size}px`,
            transform: `translate(-50%, -50%) rotate(${obj.rotation}deg)`
          }}
        >
          <div
            className="absolute w-full h-full rounded-full opacity-60"
            style={{ backgroundColor: obj.bgcolor }}
          />
          <div className="relative z-10" style={{ fontSize: `${obj.size * 0.8}px` }}>
            {obj.emoji}
          </div>
        </div>
      ))}

      {clickable && markers.map((marker, idx) => (
        <div
          key={idx}
          className="absolute border-4 border-green-500 rounded-full pointer-events-none animate-pulse"
          style={{
            left: `${marker.x}%`,
            top: `${marker.y}%`,
            width: '80px',
            height: '80px',
            transform: 'translate(-50%, -50%)'
          }}
        />
      ))}
    </div>
  );

  return (
    <div
      className="h-full flex flex-col"
      style={{ background: tenant.theme.colors.gradients.main, fontFamily: tenant.theme.fonts.body.family }}
    >
      {/* Top bar */}
      <div className="bg-black/20 backdrop-blur-sm px-3 py-2 flex items-center gap-2 flex-wrap">
        <span className="text-white font-bold text-base whitespace-nowrap">🔍 Diferencias</span>
        <div className="w-px h-5 bg-white/30 hidden sm:block" />
        <div className="flex gap-1 flex-wrap">
          {Object.entries(LEVELS).map(([key, level]) => (
            <button
              key={key}
              onClick={() => setNivel(key as DifficultadKey)}
              className={`px-2 py-0.5 rounded text-xs font-semibold transition-all ${
                nivel === key ? 'bg-white' : 'bg-white/20 text-white hover:bg-white/30'
              }`}
              style={nivel === key ? { color: tenant.theme.colors.primary } : {}}
            >
              {level.icono} {level.nombre}
            </button>
          ))}
        </div>
        <div className="flex-1" />
        <span className="text-white text-xs font-semibold">
          ⏱️{tiempoRestante}s · 🔍{diferenciasEncontradas}/{config.num_diferencias} · ❌{errores} · {themeName}
        </span>
        {/* Rules */}
        <button
          onClick={() => setShowRules(!showRules)}
          title="¿Cómo se juega?"
          className="p-1.5 rounded bg-white/20 hover:bg-white/30 text-white transition-all text-sm"
        >
          📖
        </button>
        {/* New game */}
        <button
          onClick={generateNewGame}
          title="Nuevo Tablero"
          className="p-1.5 rounded bg-white/20 hover:bg-white/30 text-white transition-all"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
          </svg>
        </button>
      </div>

      {/* Rules panel */}
      {showRules && (
        <div className="bg-white/90 px-4 py-3 text-sm text-gray-800">
          <p className="font-bold mb-1">🔍 Encuentra las Diferencias</p>
          <p>Haz clic en la imagen de la <strong>DERECHA</strong> donde veas una diferencia. Objetivo: {config.num_diferencias} diferencias en {config.tiempo_limite}s.</p>
          <p className="mt-1">Fíjate en colores, tamaños, emojis y orientaciones.</p>
        </div>
      )}

      {/* Board */}
      <div className="flex-1 flex flex-col items-center justify-center p-3">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full max-w-5xl">
          <div>
            <h3 className="text-center text-sm font-bold mb-2 text-white/90">Imagen Original</h3>
            {renderScene(escena1, false)}
          </div>
          <div>
            <h3 className="text-center text-sm font-bold mb-2 text-white/90">Encuentra las Diferencias</h3>
            {renderScene(escena2, true)}
          </div>
        </div>

        {(gameWon || gameLost) && (
          <div className={`mt-4 text-center p-4 rounded-2xl backdrop-blur-sm ${gameWon ? 'bg-green-500/30' : 'bg-red-500/30'}`}>
            <div className="text-4xl mb-2">{gameWon ? '🎉' : '⏰'}</div>
            <h2 className="text-xl font-bold text-white mb-1">
              {gameWon ? '¡Felicidades!' : '¡Se acabó el tiempo!'}
            </h2>
            <p className="text-white/80 text-sm mb-3">
              {gameWon
                ? `Encontraste todo en ${config.tiempo_limite - tiempoRestante}s con ${errores} ${errores === 1 ? 'error' : 'errores'}`
                : `Encontraste ${diferenciasEncontradas} de ${config.num_diferencias} diferencias`}
            </p>
            <button
              onClick={generateNewGame}
              className="px-6 py-2 rounded-xl font-bold text-white shadow-lg text-sm"
              style={{ background: tenant.theme.colors.primary }}
            >
              🔄 {gameWon ? 'Nuevo Tablero' : 'Intentar de Nuevo'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
